import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { projects, getProjects, getProjectsForSeeker, getProjectDisplayName, type Project } from '@data/projects';
import { getPublicPlans, type PublicPlan } from '@data/publicPlans';
import { getOffers, type Offer } from '@data/offers';
import { useAuth, type UserRole } from '@services/auth';
import { getUsers, type UserResponse } from '@services/users';
import { getPlans } from '@services/plans';
import { getAdminOfferStats, type AdminOfferStatsResponse } from '@services/offers';
import { mapApiPlanToPublicPlan } from '@utils/mapApiPlanToPublicPlan';
import {
	buildMonthlyGrowthSeries,
	type GrowthMode,
	type GrowthRange
} from '@utils/growthSeries';
import MonthlyGrowthChart from '@components/admin/MonthlyGrowthChart';
import AdminDrilldownModal from '@components/admin/AdminDrilldownModal';
import { useI18n } from '../i18n';
import { getOffersByGardener, getOffersForSeeker } from '@services/offers';
import { getTasks, type TaskResponse } from '@services/tasks';
import { mapBackendOfferToFrontend } from '@pages/seeker/OffersForMyPlan';
import {
	getMyAttendanceStatus,
	getTeamAttendance,
	checkIn,
	checkOut,
	type MyAttendanceStatus,
	type TeamAttendanceResponse
} from '@services/attendance';
import WelcomeToast from '../shared/WelcomeToast';
import { useSearchHighlight } from '../shared/useSearchHighlight';
import { getStoredCompanyScopeMode, COMPANY_WORKSPACE_CHANGED_EVENT } from '@services/companies';
import {
	getAdminActivityDrilldown,
	getAdminActivityOverview,
	type ActivityDrilldownResponse,
	type ActivityOverviewResponse
} from '@services/adminActivity';

const currencyFormatter = new Intl.NumberFormat('en-DE', {
	style: 'currency',
	currency: 'EUR',
	maximumFractionDigits: 0
});

const allProjectIds = (Array.isArray(projects) ? projects : []).map((project) => project.id);

// Fallback only when API not used (admin/company may use getAll)
const roleProjectAssignments: Record<UserRole, string[]> = {
	admin: allProjectIds,
	company: allProjectIds,
	gardener: [],
	seeker: []
};

const companyGardenerIds = ['g1', 'g2'];

const projectStatusVariant: Record<Project['status'], string> = {
	planning: 'info',
	quoted: 'warning',
	in_progress: 'primary',
	completed: 'success',
	cancelled: 'secondary'
};

type HighlightVariant = 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'secondary';

type Highlight = {
	title: string;
	value: string;
	caption?: string;
	icon: string;
	variant: HighlightVariant;
};

type UserCohortStats = {
	total: number;
	admins: number;
	companies: number;
	gardeners: number;
	seekers: number;
	freelanceGardeners: number;
	employedGardeners: number;
};

const EMPTY_USER_COHORT_STATS: UserCohortStats = {
	total: 0,
	admins: 0,
	companies: 0,
	gardeners: 0,
	seekers: 0,
	freelanceGardeners: 0,
	employedGardeners: 0
};

function computeUserCohortStats(apiUsers: UserResponse[]): UserCohortStats {
	return apiUsers.reduce<UserCohortStats>(
		(acc, account) => {
			acc.total += 1;
			if (account.role === 'admin') {
				acc.admins += 1;
			} else if (account.role === 'company') {
				acc.companies += 1;
			} else if (account.role === 'gardener') {
				acc.gardeners += 1;
				const gardenerType = (account.gardener_type || '').toLowerCase();
				if (gardenerType === 'freelance') {
					acc.freelanceGardeners += 1;
				} else {
					acc.employedGardeners += 1;
				}
			} else if (account.role === 'seeker') {
				acc.seekers += 1;
			}
			return acc;
		},
		{ ...EMPTY_USER_COHORT_STATS }
	);
}

function formatDateTime(value: string | null | undefined): string {
	if (!value) return '—';
	return new Date(value).toLocaleString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
}

export default function Dashboard() {
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const labelProjectStatus = (status: Project['status']) => {
		switch (status) {
			case 'planning':
				return copy('planning', 'Planung', 'planiranje');
			case 'quoted':
				return copy('quoted', 'angeboten', 'ponuđeno');
			case 'in_progress':
				return copy('in progress', 'in Arbeit', 'u tijeku');
			case 'completed':
				return copy('completed', 'abgeschlossen', 'završeno');
			case 'cancelled':
				return copy('cancelled', 'abgebrochen', 'otkazano');
			default:
				return String(status).replace('_', ' ');
		}
	};
	const labelOfferStatus = (status: Offer['status']) => {
		switch (status) {
			case 'pending':
				return copy('pending', 'ausstehend', 'na čekanju');
			case 'accepted':
				return copy('accepted', 'akzeptiert', 'prihvaćeno');
			case 'rejected':
				return copy('rejected', 'abgelehnt', 'odbijeno');
			default:
				return status;
		}
	};
	const labelTaskStatus = (status?: string | null) => {
		if (status === 'completed') return copy('completed', 'abgeschlossen', 'završeno');
		if (status === 'in_progress') return copy('in progress', 'in Arbeit', 'u tijeku');
		if (status === 'not_started') return copy('not started', 'nicht gestartet', 'nije započeto');
		return String(status || '').replace('_', ' ');
	};
	const [plans, setPlans] = useState<PublicPlan[]>(() => getPublicPlans());
	const [offers, setOffers] = useState<Offer[]>(() => getOffers());
	const [fetchedProjects, setFetchedProjects] = useState<Project[] | null>(null);
	const [fetchedOffers, setFetchedOffers] = useState<Offer[] | null>(null);
	const [fetchedTasks, setFetchedTasks] = useState<TaskResponse[] | null>(null);
	const [teamAttendance, setTeamAttendance] = useState<TeamAttendanceResponse | null>(null);
	const [attendance, setAttendance] = useState<MyAttendanceStatus | null>(null);
	const [attendanceLoading, setAttendanceLoading] = useState(false);
	const [attendanceBusy, setAttendanceBusy] = useState(false);
	const [workspaceListRev, setWorkspaceListRev] = useState(0);
	const [adminUserStats, setAdminUserStats] = useState<UserCohortStats | null>(null);
	const [adminDbPlans, setAdminDbPlans] = useState<PublicPlan[] | null>(null);
	const [adminOfferStatsApi, setAdminOfferStatsApi] = useState<AdminOfferStatsResponse | null>(null);
	const [adminApiUsers, setAdminApiUsers] = useState<UserResponse[]>([]);
	const [adminStatsLoading, setAdminStatsLoading] = useState(false);
	const [adminHomepageActivity, setAdminHomepageActivity] = useState<ActivityOverviewResponse | null>(null);
	const [growthRange, setGrowthRange] = useState<GrowthRange>('all');
	const [growthMode, setGrowthMode] = useState<GrowthMode>('new');
	const [growthDrilldown, setGrowthDrilldown] = useState<ActivityDrilldownResponse | null>(null);
	const [growthDrilldownLoading, setGrowthDrilldownLoading] = useState(false);
	const [growthDrilldownError, setGrowthDrilldownError] = useState<string | null>(null);
	useSearchHighlight(); // Handle search highlighting

	useEffect(() => {
		const onWorkspace = () => setWorkspaceListRev((n) => n + 1);
		window.addEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
		return () => window.removeEventListener(COMPANY_WORKSPACE_CHANGED_EVENT, onWorkspace);
	}, []);

	// Load projects, offers (and tasks for gardener) from API so dashboard shows real data for the logged-in user
	useEffect(() => {
		if (user?.id == null) return;
		const uidNum = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
		if (Number.isNaN(uidNum)) return;
		const role = user.role;

		if (role === 'gardener') {
			const scopeMode = getStoredCompanyScopeMode();
			Promise.all([
				getProjects({
					requester_user_id: uidNum,
					company_scope: scopeMode === 'all_companies' ? 'all_companies' : 'company'
				}),
				getOffersByGardener(uidNum).then((apiOffers) => apiOffers.map(mapBackendOfferToFrontend)),
				getTasks({
					requester_user_id: uidNum,
					company_scope: scopeMode === 'all_companies' ? 'all_companies' : 'company'
				})
			])
				.then(([projs, offs, taskList]) => {
					setFetchedProjects(projs);
					setFetchedOffers(offs);
					setFetchedTasks(taskList);
				})
				.catch(() => {
					setFetchedProjects([]);
					setFetchedOffers([]);
					setFetchedTasks([]);
				});
		} else if (role === 'seeker') {
			Promise.all([
				getProjectsForSeeker(uidNum),
				getOffersForSeeker(uidNum).then((apiOffers) => apiOffers.map(mapBackendOfferToFrontend))
			])
				.then(([projs, offs]) => {
					setFetchedProjects(projs);
					setFetchedOffers(offs);
				})
				.catch(() => {
					setFetchedProjects([]);
					setFetchedOffers([]);
				});
		} else if (role === 'company' || role === 'admin') {
			const scopeMode = getStoredCompanyScopeMode();
			if (role === 'admin') {
				getProjects()
					.then(setFetchedProjects)
					.catch(() => setFetchedProjects([]));
			} else {
				getProjects({
					requester_user_id: uidNum,
					company_scope: scopeMode === 'all_companies' ? 'all_companies' : 'company'
				})
					.then(setFetchedProjects)
					.catch(() => setFetchedProjects([]));
			}
			// Company: fetch team attendance (who's checked in today)
			if (role === 'company') {
				const today = new Date().toISOString().slice(0, 10);
				getTeamAttendance(String(user.id), today)
					.then(setTeamAttendance)
					.catch(() => setTeamAttendance(null));
			} else {
				setTeamAttendance(null);
			}
			// Company/admin: no per-user offers API; keep using local `offers` (fetchedOffers stays null)
		}
	}, [user?.id, user?.role, user?.activeCompanyId, workspaceListRev]);

	// Admin dashboard: refresh user/plan/offer aggregates from the database on each visit
	useEffect(() => {
		if (user?.role !== 'admin') {
			setAdminUserStats(null);
			setAdminDbPlans(null);
			setAdminOfferStatsApi(null);
			setAdminApiUsers([]);
			setAdminHomepageActivity(null);
			setAdminStatsLoading(false);
			return;
		}

		let cancelled = false;
		setAdminStatsLoading(true);

		Promise.all([
			getUsers(),
			getPlans(undefined, { includeHidden: true, includeDeleted: true }),
			getAdminOfferStats(),
			getAdminActivityOverview({ days: 30, pagePath: '/' })
		])
			.then(([apiUsers, apiPlans, offerStats, homepageActivity]) => {
				if (cancelled) return;
				setAdminUserStats(computeUserCohortStats(apiUsers));
				setAdminApiUsers(apiUsers);
				setAdminDbPlans(apiPlans.map(mapApiPlanToPublicPlan));
				setAdminOfferStatsApi(offerStats);
				setAdminHomepageActivity(homepageActivity);
			})
			.catch((err) => {
				console.warn('Failed to load admin dashboard stats', err);
				if (!cancelled) {
					setAdminUserStats(EMPTY_USER_COHORT_STATS);
					setAdminApiUsers([]);
					setAdminDbPlans([]);
					setAdminOfferStatsApi({
						total: 0,
						status: { pending: 0, accepted: 0, rejected: 0 },
						avg_mid: 0,
						accepted_today: 0
					});
					setAdminHomepageActivity(null);
				}
			})
			.finally(() => {
				if (!cancelled) setAdminStatsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [user?.role, user?.id]);

	const isEmployedGardener =
		user?.role === 'gardener' &&
		(user?.gardenerType === 'employed' || !!user?.companyName);

	useEffect(() => {
		if (!isEmployedGardener || !user?.id) return;
		setAttendanceLoading(true);
		getMyAttendanceStatus(user.id)
			.then(setAttendance)
			.catch(() => setAttendance(null))
			.finally(() => setAttendanceLoading(false));
	}, [isEmployedGardener, user?.id, user?.activeCompanyId]);

	const handleCheckIn = () => {
		if (!user?.id || attendanceBusy) return;
		setAttendanceBusy(true);
		checkIn(user.id)
			.then(() => getMyAttendanceStatus(user.id))
			.then(setAttendance)
			.catch(() => {})
			.finally(() => setAttendanceBusy(false));
	};

	const handleCheckOut = () => {
		if (!user?.id || attendanceBusy) return;
		setAttendanceBusy(true);
		checkOut(user.id)
			.then(() => getMyAttendanceStatus(user.id))
			.then(setAttendance)
			.catch(() => {})
			.finally(() => setAttendanceBusy(false));
	};

	useEffect(() => {
		const handlePlansUpdate = () => setPlans(getPublicPlans());
		window.addEventListener('publicPlansUpdated', handlePlansUpdate);
		setPlans(getPublicPlans());
		return () => {
			window.removeEventListener('publicPlansUpdated', handlePlansUpdate);
		};
	}, []);

	useEffect(() => {
		const handleOffersUpdate = () => setOffers(getOffers());
		window.addEventListener('offersUpdated', handleOffersUpdate);
		setOffers(getOffers());
		return () => {
			window.removeEventListener('offersUpdated', handleOffersUpdate);
		};
	}, []);

	if (!user) {
		return null;
	}

	const userRole = user.role;

	// All dashboard metrics below are from plans, offers, projects (localStorage/API) – no mock sensors/plants/tasks.
	const projectsForMetrics = useMemo(() => {
		if (userRole === 'admin' && fetchedProjects !== null) return fetchedProjects;
		return projects;
	}, [userRole, fetchedProjects]);

	const plansForMetrics = useMemo(() => {
		if (userRole === 'admin' && adminDbPlans !== null) return adminDbPlans;
		return plans;
	}, [userRole, adminDbPlans, plans]);

	const activeProjects = useMemo(
		() => projectsForMetrics.filter((p) => p.status === 'in_progress' || p.status === 'planning').length,
		[projectsForMetrics]
	);

	const totalProjectValue = useMemo(() => {
		return projectsForMetrics
			.filter((p) => p.status === 'in_progress' || p.status === 'planning')
			.reduce((sum, p) => sum + p.budget, 0);
	}, [projectsForMetrics]);

	const activePlans = useMemo(
		() => plansForMetrics.filter((p) => p.status === 'active' && p.moderationStatus !== 'hidden').length,
		[plansForMetrics]
	);

	const adminPublishedMarketplacePlans = useMemo(() => {
		if (userRole !== 'admin' || adminDbPlans === null) return activePlans;
		return adminDbPlans.filter(
			(p) =>
				p.status === 'active' &&
				p.moderationStatus !== 'hidden' &&
				p.moderationStatus !== 'deleted'
		).length;
	}, [userRole, adminDbPlans, activePlans]);

	const adminGrowthSeries = useMemo(() => {
		if (userRole !== 'admin') return null;
		const planDates = (adminDbPlans ?? []).map((p) => p.createdAt);
		const userDates = adminApiUsers.map((u) => u.created_at);
		return {
			users: buildMonthlyGrowthSeries(userDates, growthRange),
			plans: buildMonthlyGrowthSeries(planDates, growthRange)
		};
	}, [userRole, adminDbPlans, adminApiUsers, growthRange]);

	const openGrowthDrilldown = (kind: 'user_registrations' | 'plans_created', month: string) => {
		setGrowthDrilldownLoading(true);
		setGrowthDrilldownError(null);
		setGrowthDrilldown(null);
		getAdminActivityDrilldown({ kind, month })
			.then((data) => setGrowthDrilldown(data))
			.catch((error) => {
				setGrowthDrilldownError(
					error instanceof Error ? error.message : 'Failed to load month details'
				);
			})
			.finally(() => setGrowthDrilldownLoading(false));
	};

	const closeGrowthDrilldown = () => {
		setGrowthDrilldown(null);
		setGrowthDrilldownError(null);
		setGrowthDrilldownLoading(false);
	};

	const userProjects = useMemo(() => {
		// Use API-fetched list when available (gardener, seeker, company, admin)
		if (fetchedProjects !== null) return fetchedProjects;
		// Fallback: static list filtered by role (e.g. before first load or API error)
		if (userRole === 'admin' || userRole === 'company') {
			return projects;
		}
		const ids = roleProjectAssignments[userRole] ?? [];
		return projects.filter((project) => ids.includes(project.id));
	}, [userRole, fetchedProjects]);

	const projectStats = useMemo(() => {
		const completed = userProjects.filter((project) => project.status === 'completed');
		const inProgress = userProjects.filter((project) => project.status === 'in_progress');
		const planning = userProjects.filter((project) => project.status === 'planning');
		const quoted = userProjects.filter((project) => project.status === 'quoted');
		const upcoming = userProjects
			.filter((project) => project.dueDate && project.status !== 'completed')
			.slice()
			.sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
			.slice(0, 3);
		const totalBudget = userProjects.reduce((sum, project) => sum + project.budget, 0);
		const openBudget = userProjects
			.filter((project) => project.status !== 'completed')
			.reduce((sum, project) => sum + project.budget, 0);
		const latestCompleted =
			completed
				.slice()
				.sort((a, b) => (b.dueDate ?? '').localeCompare(a.dueDate ?? ''))[0] ?? null;

		return {
			total: userProjects.length,
			completed: completed.length,
			inProgress: inProgress.length,
			planning: planning.length,
			quoted: quoted.length,
			totalBudget,
			openBudget,
			upcoming,
			latestCompleted
		};
	}, [userProjects]);

	const userPlans = useMemo(() => {
		if (userRole === 'admin' || userRole === 'company') {
			return plans;
		}
		if (userRole === 'seeker' && user?.id) {
			return plans.filter((plan) => plan.seekerUserId === String(user.id));
		}
		return plans;
	}, [plans, userRole, user?.id]);

	const userOffers = useMemo(() => {
		if (fetchedOffers !== null) return fetchedOffers;
		if (userRole === 'admin') return offers;
		if (userRole === 'company') {
			return offers.filter((offer) => companyGardenerIds.includes(offer.gardenerId));
		}
		if (userRole === 'gardener') {
			return offers.filter((offer) => offer.gardenerId === String(user?.id));
		}
		if (userRole === 'seeker') {
			const myPlanIds = userPlans.map((p) => p.id);
			return offers.filter((offer) => myPlanIds.includes(offer.planId));
		}
		return [];
	}, [offers, userRole, user?.id, fetchedOffers, userPlans]);

	const offersByStatus = useMemo(() => {
		return userOffers.reduce(
			(acc, offer) => {
				acc[offer.status] = (acc[offer.status] ?? 0) + 1;
				return acc;
			},
			{ pending: 0, accepted: 0, rejected: 0 } as Record<Offer['status'], number>
		);
	}, [userOffers]);

	const averageOfferValue = useMemo(() => {
		if (userOffers.length === 0) {
			return 0;
		}
		const total = userOffers.reduce((sum, offer) => sum + offer.bands.mid, 0);
		return total / userOffers.length;
	}, [userOffers]);

	const highestValueProject = useMemo(() => {
		return (
			userProjects.reduce<Project | null>((current, project) => {
				if (!current || project.budget > current.budget) {
					return project;
				}
				return current;
			}, null) ?? null
		);
	}, [userProjects]);

	const adminPlanStats = useMemo(() => {
		const summary = plansForMetrics.reduce(
			(acc, plan) => {
				acc.total += 1;
				acc.status[plan.status] = (acc.status[plan.status] ?? 0) + 1;
				if (plan.moderationStatus === 'hidden') {
					acc.hidden += 1;
				} else {
					acc.visible += 1;
				}
				acc.seekers.add(plan.seekerUserId);
				return acc;
			},
			{
				total: 0,
				visible: 0,
				hidden: 0,
				status: { draft: 0, active: 0, closed: 0 } as Record<PublicPlan['status'], number>,
				seekers: new Set<string>()
			}
		);

		return {
			total: summary.total,
			visible: summary.visible,
			hidden: summary.hidden,
			active: summary.status.active ?? 0,
			draft: summary.status.draft ?? 0,
			closed: summary.status.closed ?? 0,
			seekers: summary.seekers.size
		};
	}, [plansForMetrics]);

	const adminOfferStats = useMemo(() => {
		if (userRole === 'admin' && adminOfferStatsApi !== null) {
			const api = adminOfferStatsApi;
			return {
				total: api.total,
				status: api.status,
				avgMid: api.avg_mid,
				acceptanceRate: api.total === 0 ? 0 : api.status.accepted / api.total,
				acceptedToday: api.accepted_today
			};
		}

		const summary = offers.reduce(
			(acc, offer) => {
				acc.total += 1;
				acc.status[offer.status] = (acc.status[offer.status] ?? 0) + 1;
				acc.midSum += offer.bands.mid;
				return acc;
			},
			{
				total: 0,
				status: { pending: 0, accepted: 0, rejected: 0 } as Record<Offer['status'], number>,
				midSum: 0
			}
		);

		return {
			total: summary.total,
			status: summary.status,
			avgMid: summary.total === 0 ? 0 : summary.midSum / summary.total,
			acceptanceRate: summary.total === 0 ? 0 : summary.status.accepted / summary.total,
			acceptedToday: summary.status.accepted
		};
	}, [offers, userRole, adminOfferStatsApi]);

	const activePlanCount = userPlans.filter((plan) => plan.status === 'active').length;
	const closedPlanCount = userPlans.filter((plan) => plan.status === 'closed').length;
	const pendingOffers = offersByStatus.pending ?? 0;
	const acceptedOffers = offersByStatus.accepted ?? 0;
	const rejectedOffers = offersByStatus.rejected ?? 0;
	const openPipelineCount = projectStats.planning + projectStats.quoted;
	const latestCompletedCaption =
		projectStats.completed > 0 && projectStats.latestCompleted
			? `${copy('Last completed', 'Zuletzt abgeschlossen', 'Zadnje završeno')}: ${projectStats.latestCompleted.name}`
			: copy('No completed projects yet', 'Noch keine abgeschlossenen Projekte', 'Još nema završenih projekata');
	const averageOfferLabel = averageOfferValue > 0 ? currencyFormatter.format(Math.round(averageOfferValue)) : currencyFormatter.format(0);
	const openBudgetLabel = currencyFormatter.format(projectStats.openBudget);
	const totalBudgetLabel = currencyFormatter.format(projectStats.totalBudget);
	const adminSupplyDemand =
		adminPlanStats.visible === 0
			? '—'
			: (adminOfferStats.total / adminPlanStats.visible).toFixed(1);

	// Gardener: tasks from API (getTasks({ user_id }))
	const assignedTasksList = useMemo(() => fetchedTasks ?? [], [fetchedTasks]);
	const tasksDueSoonCount = useMemo(() => {
		const now = Date.now();
		const threeDays = 3 * 24 * 60 * 60 * 1000;
		return assignedTasksList.filter((t) => {
			if (t.status === 'completed') return false;
			const due = t.due_date || t.end_date;
			if (!due) return false;
			const dueTime = new Date(due).getTime();
			return dueTime >= now && dueTime - now <= threeDays;
		}).length;
	}, [assignedTasksList]);
	const tasksOverdueCount = useMemo(
		() =>
			assignedTasksList.filter((t) => {
				if (t.status === 'completed') return false;
				const due = t.due_date || t.end_date;
				return due ? new Date(due).getTime() < Date.now() : false;
			}).length,
		[assignedTasksList]
	);
	const tasksCompletedCount = useMemo(
		() => assignedTasksList.filter((t) => t.status === 'completed').length,
		[assignedTasksList]
	);

	const resolvedAdminUserStats = adminUserStats ?? EMPTY_USER_COHORT_STATS;
	const formatAdminStat = (value: number) => (adminStatsLoading ? '…' : String(value));

	const roleHighlights = useMemo<Highlight[]>(() => {
		switch (userRole) {
			case 'seeker':
				return [
					{
						title: 'Projects in progress',
						value: String(projectStats.inProgress),
						caption: `Pipeline total: ${projectStats.total}`,
						icon: 'bi-kanban',
						variant: 'primary'
					},
					{
						title: 'Completed projects',
						value: String(projectStats.completed),
						caption: latestCompletedCaption,
						icon: 'bi-patch-check-fill',
						variant: 'success'
					},
					{
						title: 'Planning & quotes',
						value: String(openPipelineCount),
						caption: 'Waiting on confirmation',
						icon: 'bi-clipboard-check',
						variant: 'info'
					},
					{
						title: 'Offers pending',
						value: String(pendingOffers),
						caption: `${userOffers.length} total responses`,
						icon: 'bi-envelope-open',
						variant: 'warning'
					},
					{
						title: 'Draft plans',
						value: String(userPlans.filter((p) => p.status === 'draft').length),
						caption: 'Not yet published',
						icon: 'bi-pencil-square',
						variant: 'secondary'
					}
				];
			case 'gardener':
				return [
					{
						title: copy('Total projects', 'Gesamtprojekte', 'Ukupno projekata'),
						value: String(projectStats.total),
						caption: `${copy('Open pipeline', 'Offene Pipeline', 'Otvorenih u tijeku')}: ${openPipelineCount}`,
						icon: 'bi-collection',
						variant: 'secondary'
					},
					{
						title: copy('Jobs in progress', 'Laufende Einsätze', 'Radovi u tijeku'),
						value: String(projectStats.inProgress),
						caption: openBudgetLabel,
						icon: 'bi-tools',
						variant: 'primary'
					},
					{
						title: copy('Completed jobs', 'Abgeschlossene Einsätze', 'Završeni radovi'),
						value: String(projectStats.completed),
						caption: latestCompletedCaption,
						icon: 'bi-check-circle-fill',
						variant: 'success'
					},
					{
						title: copy('My offers', 'Meine Angebote', 'Moje ponude'),
						value: String(userOffers.length),
						caption: `${pendingOffers} ${copy('pending', 'ausstehend', 'na čekanju')} · ${acceptedOffers} ${copy('accepted', 'akzeptiert', 'prihvaćeno')}`,
						icon: 'bi-envelope-paper',
						variant: 'warning'
					},
					{
						title: copy('Assigned tasks', 'Zugewiesene Aufgaben', 'Dodijeljeni zadaci'),
						value: String(assignedTasksList.length),
						caption: `${tasksDueSoonCount} ${copy('due soon', 'bald fällig', 'uskoro')} · ${tasksCompletedCount} ${copy('done', 'erledigt', 'gotovo')}`,
						icon: 'bi-check2-square',
						variant: 'info'
					},
					{
						title: copy('Open plans to quote', 'Offene Pläne für Angebote', 'Otvoreni planovi za ponudu'),
						value: String(activePlans),
						caption: copy('In marketplace', 'Im Marktplatz', 'Na tržnici'),
						icon: 'bi-geo-alt',
						variant: 'secondary'
					}
				];
			case 'company':
				return [
					{
						title: 'Portfolio projects',
						value: String(projectStats.total),
						caption: totalBudgetLabel,
						icon: 'bi-briefcase',
						variant: 'secondary'
					},
					{
						title: 'Active delivery',
						value: String(projectStats.inProgress),
						caption: `${openPipelineCount} in planning/quote`,
						icon: 'bi-diagram-3',
						variant: 'primary'
					},
					{
						title: 'Completed engagements',
						value: String(projectStats.completed),
						caption: latestCompletedCaption,
						icon: 'bi-award',
						variant: 'success'
					},
					{
						title: 'Team active today',
						value: teamAttendance != null ? `${teamAttendance.checked_in_count} / ${teamAttendance.total_count}` : '—',
						caption: teamAttendance != null ? 'Gardeners checked in' : 'Loading…',
						icon: 'bi-people-fill',
						variant: 'info'
					},
					{
						title: 'Average offer (mid)',
						value: averageOfferLabel,
						caption: `${pendingOffers} pending · ${acceptedOffers} won`,
						icon: 'bi-currency-euro',
						variant: 'secondary'
					}
				];
			case 'admin':
				return [
					{
						title: 'Registered gardeners',
						value: formatAdminStat(resolvedAdminUserStats.gardeners),
						caption: adminStatsLoading
							? copy('Loading…', 'Laden…', 'Učitavanje…')
							: `${resolvedAdminUserStats.freelanceGardeners} freelance · ${resolvedAdminUserStats.employedGardeners} employed`,
						icon: 'bi-flower3',
						variant: 'success'
					},
					{
						title: 'Garden seekers',
						value: formatAdminStat(resolvedAdminUserStats.seekers),
						caption: adminStatsLoading
							? copy('Loading…', 'Laden…', 'Učitavanje…')
							: `${adminPlanStats.active} active plans`,
						icon: 'bi-people',
						variant: 'primary'
					},
					{
						title: 'Partner companies',
						value: formatAdminStat(resolvedAdminUserStats.companies),
						caption: adminStatsLoading
							? copy('Loading…', 'Laden…', 'Učitavanje…')
							: `${adminOfferStats.total} offers tracked`,
						icon: 'bi-building',
						variant: 'info'
					},
					{
						title: 'Active projects',
						value: String(projectStats.inProgress),
						caption: `${projectStats.total} total projects`,
						icon: 'bi-kanban',
						variant: 'warning'
					}
				];
			default:
				return [];
		}
	}, [
		userRole,
		projectStats,
		latestCompletedCaption,
		openPipelineCount,
		pendingOffers,
		userOffers.length,
		userPlans,
		openBudgetLabel,
		totalBudgetLabel,
		averageOfferLabel,
		acceptedOffers,
		adminPlanStats,
		adminOfferStats,
		resolvedAdminUserStats,
		adminStatsLoading,
		assignedTasksList.length,
		tasksDueSoonCount,
		tasksCompletedCount,
		activePlans,
		teamAttendance
	]);

	const formatDate = (value?: string) => {
		if (!value) {
			return 'TBC';
		}
		return new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
	};

	const roleDetailSection = useMemo(() => {
		switch (userRole) {
			case 'seeker':
				return (
					<div className="row g-3 mt-1">
						<div className="col-12 col-lg-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Project pipeline', 'Projektpipeline', 'Projektni tijek')}</span>
									<span className="badge text-bg-light">{projectStats.total || '0'} {copy('total', 'gesamt', 'ukupno')}</span>
								</div>
								<ul className="list-group list-group-flush">
									{userProjects.length === 0 && <li className="list-group-item text-muted small">{copy('No projects in your pipeline yet.', 'Noch keine Projekte in Ihrer Pipeline.', 'Još nema projekata u vašem tijeku.')}</li>}
									{userProjects.map((project) => (
										<li key={project.id} className="list-group-item d-flex justify-content-between align-items-center">
											<div>
												<div className="fw-medium">{getProjectDisplayName(project)}</div>
												<div className="text-muted small">{project.client}</div>
											</div>
											<span className={`badge text-bg-${projectStatusVariant[project.status]}`}>
												{labelProjectStatus(project.status)}
											</span>
										</li>
									))}
								</ul>
							</div>
						</div>
						<div className="col-12 col-lg-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Offers overview', 'Angebotsübersicht', 'Pregled ponuda')}</span>
									<span className="badge text-bg-light">{userOffers.length} {copy('total', 'gesamt', 'ukupno')}</span>
								</div>
								<div className="card-body">
									{userOffers.length === 0 ? (
										<p className="text-muted small mb-0">{copy('You’ll see responses from gardeners as soon as offers arrive.', 'Sobald Angebote eintreffen, sehen Sie hier Antworten von Gärtnern.', 'Odgovore vrtlara vidjet ćete čim ponude stignu.')}</p>
									) : (
										<div className="vstack gap-2">
											{(['pending', 'accepted', 'rejected'] as Offer['status'][]).map((status) => (
												<div key={status} className="d-flex justify-content-between align-items-center">
													<span className="text-capitalize">{labelOfferStatus(status)}</span>
													<span
														className={`badge text-bg-${
															status === 'accepted' ? 'success' : status === 'pending' ? 'warning' : 'secondary'
														}`}
													>
														{offersByStatus[status] ?? 0}
													</span>
												</div>
											))}
										</div>
									)}
									<hr />
									<div className="d-flex justify-content-between text-muted small">
										<span>{copy('Plans active', 'Aktive Pläne', 'Aktivni planovi')}</span>
										<span>
											{activePlanCount} active · {closedPlanCount} closed
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				);
			case 'gardener':
				return (
					<div className="row g-3 mt-1">
						<div className="col-12 col-lg-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Assigned tasks', 'Zugewiesene Aufgaben', 'Dodijeljeni zadaci')}</span>
									<span className="badge text-bg-light">{assignedTasksList.length}</span>
								</div>
								<ul className="list-group list-group-flush">
									{assignedTasksList.length === 0 && (
										<li className="list-group-item text-muted small">{copy('No tasks assigned. Tasks come from your projects.', 'Keine Aufgaben zugewiesen. Aufgaben kommen aus Ihren Projekten.', 'Nema dodijeljenih zadataka. Zadaci dolaze iz vaših projekata.')}</li>
									)}
									{assignedTasksList.slice(0, 6).map((task) => (
										<li key={task.id} className="list-group-item d-flex justify-content-between align-items-center">
											<div>
												<div className="fw-medium">{task.title}</div>
												<div className="text-muted small">
													{copy('Due', 'Fällig', 'Rok')} {formatDate(task.due_date || task.end_date)}
												</div>
											</div>
											<span
												className={`badge rounded-pill ${
													task.status === 'completed'
														? 'text-bg-success'
														: task.status === 'in_progress'
															? 'text-bg-warning'
															: 'text-bg-secondary'
												}`}
											>
												{labelTaskStatus(task.status)}
											</span>
										</li>
									))}
								</ul>
								{assignedTasksList.length > 6 && (
									<div className="card-footer bg-transparent border-0">
										<Link to="/app/tasks" className="small">{copy('View all tasks', 'Alle Aufgaben anzeigen', 'Prikaži sve zadatke')} →</Link>
									</div>
								)}
							</div>
						</div>
						<div className="col-12 col-lg-6">
							<div className="card h-100">
								<div className="card-header">{copy('Upcoming site checkpoints', 'Bevorstehende Baustellen-Meilensteine', 'Nadolazeće točke na lokaciji')}</div>
								<ul className="list-group list-group-flush">
									{projectStats.upcoming.length === 0 && (
										<li className="list-group-item text-muted small">{copy('No upcoming visits scheduled.', 'Keine kommenden Besuche geplant.', 'Nema planiranih nadolazećih posjeta.')}</li>
									)}
									{projectStats.upcoming.map((project) => (
										<li key={project.id} className="list-group-item d-flex justify-content-between align-items-center">
											<div>
												<div className="fw-medium">{getProjectDisplayName(project)}</div>
												<div className="text-muted small">{labelProjectStatus(project.status)} • {project.client}</div>
											</div>
											<span className="badge text-bg-light">{formatDate(project.dueDate)}</span>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				);
			case 'company':
				return (
					<div className="row g-3 mt-1">
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Portfolio snapshot', 'Portfolio-Überblick', 'Pregled portfelja')}</span>
									<span className="badge text-bg-light">{projectStats.total} {copy('projects', 'Projekte', 'projekata')}</span>
								</div>
								<div className="card-body">
									<div className="table-responsive">
										<table className="table table-sm align-middle">
											<thead className="text-muted small text-uppercase">
												<tr>
													<th>{copy('Project', 'Projekt', 'Projekt')}</th>
													<th>{copy('Status', 'Status', 'Status')}</th>
													<th className="text-end">{copy('Budget', 'Budget', 'Budžet')}</th>
												</tr>
											</thead>
											<tbody>
												{userProjects.slice(0, 6).map((project) => (
													<tr key={project.id}>
														<td>
															<div className="fw-medium">{getProjectDisplayName(project)}</div>
															<div className="text-muted small">{project.client}</div>
														</td>
														<td>
															<span className={`badge text-bg-${projectStatusVariant[project.status]}`}>
																{labelProjectStatus(project.status)}
															</span>
														</td>
														<td className="text-end">{currencyFormatter.format(project.budget)}</td>
													</tr>
												))}
												{userProjects.length === 0 && (
													<tr>
														<td colSpan={3} className="text-center text-muted small">
															{copy('No projects in portfolio yet.', 'Noch keine Projekte im Portfolio.', 'Još nema projekata u portfelju.')}
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Offer pipeline', 'Angebotspipeline', 'Tijek ponuda')}</span>
									<span className="badge text-bg-light">{userOffers.length} {copy('responses', 'Antworten', 'odgovora')}</span>
								</div>
								<div className="card-body">
									{userOffers.length === 0 ? (
										<p className="text-muted small mb-0">{copy('No incoming offers tracked at the moment.', 'Aktuell werden keine eingehenden Angebote erfasst.', 'Trenutačno nema evidentiranih dolaznih ponuda.')}</p>
									) : (
										<div className="vstack gap-2">
											<div className="d-flex justify-content-between">
												<span>{copy('Pending', 'Ausstehend', 'Na čekanju')}</span>
												<span className="badge text-bg-warning">{pendingOffers}</span>
											</div>
											<div className="d-flex justify-content-between">
												<span>{copy('Accepted', 'Akzeptiert', 'Prihvaćeno')}</span>
												<span className="badge text-bg-success">{acceptedOffers}</span>
											</div>
											<div className="d-flex justify-content-between">
												<span>{copy('Rejected', 'Abgelehnt', 'Odbijeno')}</span>
												<span className="badge text-bg-secondary">{rejectedOffers}</span>
											</div>
											<div className="d-flex justify-content-between border-top pt-2 mt-1">
												<span>{copy('Average mid band', 'Durchschnittlicher Mittelwert', 'Prosječni srednji raspon')}</span>
												<span className="fw-semibold">{averageOfferLabel}</span>
											</div>
										</div>
									)}
									{highestValueProject && (
										<div className="mt-3 text-muted small">
											{copy('Top budget currently:', 'Höchstes Budget aktuell:', 'Trenutno najveći budžet:')}{' '}
											<span className="fw-semibold">{getProjectDisplayName(highestValueProject)}</span> (
											{currencyFormatter.format(highestValueProject.budget)})
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				);
			case 'admin':
				return (
					<div className="row g-3 mt-1">
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Platform status', 'Plattformstatus', 'Status platforme')}</span>
									<span className="badge text-bg-light">
										{formatAdminStat(resolvedAdminUserStats.total)} {copy('users', 'Nutzer', 'korisnika')}
									</span>
								</div>
								<div className="card-body">
									<div className="row g-3">
										<div className="col-6">
											<div className="border rounded p-3 h-100 bg-light bg-opacity-50">
												<div className="d-flex align-items-center justify-content-between mb-2">
													<i className="bi bi-flower3 fs-3 text-success"></i>
													<div className="text-end">
														<div className="fs-2 fw-bold">{formatAdminStat(resolvedAdminUserStats.gardeners)}</div>
														<div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{copy('Gardeners', 'Gärtner', 'Vrtlari')}</div>
													</div>
												</div>
												<div className="border-top pt-2 mt-2">
													<div className="text-muted small d-flex justify-content-between">
														<span>{copy('Freelance', 'Freiberuflich', 'Samostalni')}</span>
														<span className="fw-semibold">{formatAdminStat(resolvedAdminUserStats.freelanceGardeners)}</span>
													</div>
													<div className="text-muted small d-flex justify-content-between">
														<span>{copy('Employed', 'Angestellt', 'Zaposleni')}</span>
														<span className="fw-semibold">{formatAdminStat(resolvedAdminUserStats.employedGardeners)}</span>
													</div>
												</div>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-3 h-100 bg-light bg-opacity-50">
												<div className="d-flex align-items-center justify-content-between mb-2">
													<i className="bi bi-people fs-3 text-primary"></i>
													<div className="text-end">
														<div className="fs-2 fw-bold">{formatAdminStat(resolvedAdminUserStats.seekers)}</div>
														<div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{copy('Garden seekers', 'Gartensuchende', 'Vlasnici vrta')}</div>
													</div>
												</div>
												<div className="border-top pt-2 mt-2">
													<div className="text-muted small d-flex justify-content-between">
														<span>{copy('Active plans', 'Aktive Pläne', 'Aktivni planovi')}</span>
														<span className="fw-semibold">{adminPlanStats.active}</span>
													</div>
												</div>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-3 h-100 bg-light bg-opacity-50">
												<div className="d-flex align-items-center justify-content-between">
													<i className="bi bi-building fs-3 text-info"></i>
													<div className="text-end">
														<div className="fs-2 fw-bold">{formatAdminStat(resolvedAdminUserStats.companies)}</div>
														<div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{copy('Partner companies', 'Partnerunternehmen', 'Partnerske tvrtke')}</div>
													</div>
												</div>
											</div>
										</div>
										<div className="col-6">
											<div className="border rounded p-3 h-100 bg-light bg-opacity-50">
												<div className="d-flex align-items-center justify-content-between mb-2">
													<i className="bi bi-grid fs-3 text-warning"></i>
													<div className="text-end">
														<div className="fs-2 fw-bold">{adminPlanStats.visible}</div>
														<div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>{copy('Visible plans', 'Sichtbare Pläne', 'Vidljivi planovi')}</div>
													</div>
												</div>
												<div className="border-top pt-2 mt-2">
													<div className="text-muted small d-flex justify-content-between">
														<span>{copy('Draft', 'Entwurf', 'Skica')}</span>
														<span className="fw-semibold">{adminPlanStats.draft}</span>
													</div>
													<div className="text-muted small d-flex justify-content-between">
														<span>{copy('Hidden', 'Verborgen', 'Skriveno')}</span>
														<span className="fw-semibold">{adminPlanStats.hidden}</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-12 col-xl-6">
							<div className="card h-100">
								<div className="card-header d-flex justify-content-between align-items-center">
									<span>{copy('Operational focus', 'Operativer Fokus', 'Operativni fokus')}</span>
									{adminOfferStats.status.pending > 0 && (
										<span className="badge text-bg-warning">{adminOfferStats.status.pending} {copy('pending', 'ausstehend', 'na čekanju')}</span>
									)}
								</div>
								<div className="card-body">
									<div className="vstack gap-2 mb-3">
										<div className="d-flex justify-content-between align-items-center">
											<span>{copy('Pending offers', 'Ausstehende Angebote', 'Ponude na čekanju')}</span>
											<span className="fw-semibold">{adminOfferStats.status.pending ?? 0}</span>
										</div>
										<div className="d-flex justify-content-between text-muted small">
											<span>{copy('Accepted today', 'Heute angenommen', 'Danas prihvaćeno')}</span>
											<span>{adminStatsLoading ? '…' : (adminOfferStats.acceptedToday ?? 0)}</span>
										</div>
										<div 
											className="d-flex justify-content-between border-top pt-2 mt-1"
											data-section-id="supply-demand"
										>
											<span>{copy('Supply vs demand', 'Angebot und Nachfrage', 'Ponuda i potražnja')}</span>
											<span className="fw-semibold">{adminSupplyDemand}</span>
										</div>
									</div>
									<div className="fw-semibold text-muted small text-uppercase mb-2">{copy('Upcoming checkpoints', 'Bevorstehende Meilensteine', 'Nadolazeće točke')}</div>
									<ul className="list-group list-group-flush">
										{projectStats.upcoming.length === 0 && (
											<li className="list-group-item text-muted small px-0">{copy('No upcoming milestones scheduled.', 'Keine kommenden Meilensteine geplant.', 'Nema planiranih nadolazećih prekretnica.')}</li>
										)}
										{projectStats.upcoming.map((project) => (
											<li key={project.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
												<div>
													<div className="fw-medium">{getProjectDisplayName(project)}</div>
													<div className="text-muted small">{project.status.replace('_', ' ')}</div>
												</div>
												<span className="badge text-bg-light">{formatDate(project.dueDate)}</span>
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					</div>
				);
			default:
				return null;
		}
	}, [
		userRole,
		projectStats,
		userProjects,
		userOffers,
		offersByStatus,
		activePlanCount,
		closedPlanCount,
		pendingOffers,
		acceptedOffers,
		rejectedOffers,
		averageOfferLabel,
		highestValueProject,
		adminPlanStats,
		adminOfferStats,
		adminSupplyDemand,
		assignedTasksList
	]);

	const roleDisplayLabel = useMemo(() => {
		switch (userRole) {
			case 'admin':
				return copy('Admin', 'Admin', 'Admin');
			case 'company':
				return copy('Company', 'Unternehmen', 'Tvrtka');
			case 'gardener':
				return user.gardenerType === 'freelance'
					? copy('Freelance Gardener', 'Freiberuflicher Gärtner', 'Samostalni vrtlar')
					: copy('Gardener', 'Gärtner', 'Vrtlar');
			case 'seeker':
				return copy('Seeker', 'Gartensuchende', 'Vlasnik vrta');
			default:
				return userRole;
		}
	}, [userRole, user.gardenerType, language]);

	return (
		<>
			<WelcomeToast />
			<AdminDrilldownModal
				open={growthDrilldownLoading || growthDrilldown !== null || !!growthDrilldownError}
				title={
					growthDrilldown?.kind === 'plans_created'
						? copy('Plans created', 'Erstellte Pläne', 'Kreirani planovi')
						: copy('Users registered', 'Registrierte Nutzer', 'Registrirani korisnici')
				}
				subtitle={
					growthDrilldown && 'month' in growthDrilldown
						? new Date(`${growthDrilldown.month}-01T00:00:00`).toLocaleDateString('en-GB', {
								month: 'long',
								year: 'numeric'
							})
						: undefined
				}
				loading={growthDrilldownLoading}
				error={growthDrilldownError}
				onClose={closeGrowthDrilldown}
			>
				{growthDrilldown?.kind === 'user_registrations' ? (
					<div className="table-responsive">
						<table className="table table-sm align-middle mb-0">
							<thead className="text-muted small text-uppercase">
								<tr>
									<th>User</th>
									<th>Role</th>
									<th>Company</th>
									<th className="text-end">Registered</th>
								</tr>
							</thead>
							<tbody>
								{growthDrilldown.users.map((userRow) => (
									<tr key={userRow.id}>
										<td>
											<div className="fw-semibold">{userRow.name || userRow.email}</div>
											<div className="small text-muted">{userRow.email}</div>
										</td>
										<td className="text-capitalize">{userRow.role}</td>
										<td>{userRow.company_name || '—'}</td>
										<td className="text-end">{formatDateTime(userRow.created_at)}</td>
									</tr>
								))}
								{!growthDrilldown.users.length && (
									<tr>
										<td colSpan={4} className="text-muted small">
											No users in that month.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				) : null}
				{growthDrilldown?.kind === 'plans_created' ? (
					<div className="table-responsive">
						<table className="table table-sm align-middle mb-0">
							<thead className="text-muted small text-uppercase">
								<tr>
									<th>Plan</th>
									<th>Owner</th>
									<th>Status</th>
									<th className="text-end">Created</th>
								</tr>
							</thead>
							<tbody>
								{growthDrilldown.plans.map((plan) => (
									<tr key={plan.id}>
										<td>
											<div className="fw-semibold">{plan.title || `Plan #${plan.id}`}</div>
											<div className="small text-muted">#{plan.id}</div>
										</td>
										<td>
											<div>{plan.owner_name || 'Unknown user'}</div>
											<div className="small text-muted">{plan.owner_email || '—'}</div>
										</td>
										<td>
											<span className="text-capitalize">{plan.status}</span>
											{plan.moderation_status ? (
												<div className="small text-muted text-capitalize">{plan.moderation_status}</div>
											) : null}
										</td>
										<td className="text-end">{formatDateTime(plan.created_at)}</td>
									</tr>
								))}
								{!growthDrilldown.plans.length && (
									<tr>
										<td colSpan={4} className="text-muted small">
											No plans in that month.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				) : null}
			</AdminDrilldownModal>
			<div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
				<div>
					<h1 className="h3 mb-1">{copy('Dashboard', 'Übersicht', 'Nadzorna ploča')}</h1>
					{userRole === 'admin' && (
						<p className="text-muted small mb-0">
							{copy('Operational overview', 'Betriebsübersicht', 'Operativni pregled')} · <Link to="/app/admin/analytics" className="text-decoration-none" style={{ color: '#2f9e59' }}>{copy('View insights & trends', 'Einblicke & Trends ansehen', 'Pogledaj uvide i trendove')} →</Link>
						</p>
					)}
				</div>
				{/* Quick actions per role */}
				<div className="d-flex flex-wrap gap-2">
					{userRole === 'seeker' && (
						<Link to="/app/seeker/create-plan" className="btn btn-success btn-sm">
							<i className="bi bi-plus-circle me-1"></i>{copy('Create plan', 'Plan erstellen', 'Kreiraj plan')}
						</Link>
					)}
					{userRole === 'seeker' && (
						<Link to="/app/seeker/plans" className="btn btn-outline-secondary btn-sm">
							<i className="bi bi-journal-text me-1"></i>{copy('My plans', 'Meine Pläne', 'Moji planovi')}
						</Link>
					)}
					{(userRole === 'gardener' || userRole === 'company') && (
						<Link to="/app/plans" className="btn btn-success btn-sm">
							<i className="bi bi-shop me-1"></i>{copy('Marketplace', 'Marktplatz', 'Tržnica')}
						</Link>
					)}
					{(userRole === 'gardener' || userRole === 'company') && (
						<Link to="/app/projects" className="btn btn-outline-secondary btn-sm">
							<i className="bi bi-kanban me-1"></i>{copy('Projects', 'Projekte', 'Projekti')}
						</Link>
					)}
					{userRole === 'admin' && (
						<Link to="/app/admin/moderation" className="btn btn-outline-warning btn-sm">
							<i className="bi bi-shield-check me-1"></i>{copy('Moderation', 'Moderation', 'Moderacija')}
						</Link>
					)}
				</div>
			</div>

			{/* REMOVED: Plants / Open tasks / Avg moisture cards – used mock @data/plants, @data/tasks, @data/metrics. Restore when real plant/task/sensor data is available. */}

			{isEmployedGardener && (
				<div className="card mb-4 border-success border-2">
					<div className="card-body py-3">
						<div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
							<div className="d-flex align-items-center gap-3">
								<i className="bi bi-clock-history fs-2 text-success"></i>
								<div>
									<div className="fw-bold">{copy('Check-in for the day', 'Check-in für heute', 'Današnja prijava')}</div>
									<div className="text-muted small">
										{attendanceLoading
											? copy('Loading…', 'Lädt…', 'Učitavanje…')
											: attendance?.status === 'checked_in' && attendance?.today?.check_in_at
												? `${copy('Checked in at', 'Eingecheckt um', 'Prijavljen u')} ${new Date(attendance.today.check_in_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
												: attendance?.status === 'checked_out'
													? copy('Checked out for today', 'Heute ausgecheckt', 'Odjavljen za danas')
													: attendance?.status === 'not_applicable'
														? attendance.message ?? ''
														: copy('Ready to check in', 'Bereit zum Einchecken', 'Spremno za prijavu')}
									</div>
								</div>
							</div>
							{!attendanceLoading && (attendance?.can_check_in ?? true) && (
								<button
									type="button"
									className="btn btn-success btn-lg"
									onClick={handleCheckIn}
									disabled={attendanceBusy}
								>
									{attendanceBusy ? (
										<span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
									) : (
										<i className="bi bi-box-arrow-in-right me-2"></i>
									)}
									{copy('Check in', 'Einchecken', 'Prijavi se')}
								</button>
							)}
							{!attendanceLoading && attendance?.can_check_out && (
								<button
									type="button"
									className="btn btn-outline-secondary btn-lg"
									onClick={handleCheckOut}
									disabled={attendanceBusy}
								>
									{attendanceBusy ? (
										<span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
									) : (
										<i className="bi bi-box-arrow-right me-1"></i>
									)}
									{copy('Check out', 'Auschecken', 'Odjavi se')}
								</button>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Analytics Overview: role-appropriate metrics */}
			<div className="row g-3 mt-1">
				<div className="col-12">
					<div className="card animate-fade-in-up stagger-3 hover-lift">
						<div className="card-header d-flex align-items-center justify-content-between">
							<span>
								{userRole === 'seeker' && copy('Your activity', 'Deine Aktivität', 'Tvoja aktivnost')}
								{userRole === 'company' && copy('Portfolio overview', 'Portfolio-Übersicht', 'Pregled portfelja')}
								{(userRole === 'gardener' || userRole === 'admin') && copy('Analytics Overview', 'Analyseübersicht', 'Pregled analitike')}
							</span>
							<small className="text-muted">
								{userRole === 'gardener' && copy('Last 7 days', 'Letzte 7 Tage', 'Zadnjih 7 dana')}
								{userRole === 'admin' && copy('Platform snapshot', 'Plattform-Überblick', 'Pregled platforme')}
							</small>
						</div>
						<div className="card-body">
							<div className="row g-4">
								{userRole === 'seeker' && (
									<>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Active plans', 'Aktive Pläne', 'Aktivni planovi')}</div>
												<div className="fs-3 fw-bold text-info count-up">{activePlanCount}</div>
												<small className="text-muted">{copy('Listed for quotes', 'Für Angebote gelistet', 'Objavljeno za ponude')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Offers received', 'Erhaltene Angebote', 'Primljene ponude')}</div>
												<div className="fs-3 fw-bold text-warning count-up">{pendingOffers + acceptedOffers + rejectedOffers}</div>
												<small className="text-muted">{pendingOffers} {copy('pending', 'ausstehend', 'na čekanju')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Projects in progress', 'Projekte in Arbeit', 'Projekti u tijeku')}</div>
												<div className="fs-3 fw-bold text-primary count-up">{projectStats.inProgress}</div>
												<small className="text-muted">{copy('Underway', 'In Umsetzung', 'U provedbi')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Planning & quotes', 'Planung & Angebote', 'Planiranje i ponude')}</div>
												<div className="fs-3 fw-bold count-up">{openPipelineCount}</div>
												<small className="text-muted">{copy('In pipeline', 'In der Pipeline', 'U tijeku')}</small>
											</div>
										</div>
									</>
								)}
								{userRole === 'company' && (
									<>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Portfolio projects', 'Portfolio-Projekte', 'Portfelj projekata')}</div>
												<div className="fs-3 fw-bold count-up">{projectStats.total}</div>
												<small className="text-muted">{currencyFormatter.format(projectStats.totalBudget)} {copy('total', 'gesamt', 'ukupno')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Active delivery', 'Aktive Umsetzung', 'Aktivna izvedba')}</div>
												<div className="fs-3 fw-bold text-primary count-up">{projectStats.inProgress}</div>
												<small className="text-muted">{openPipelineCount} {copy('in planning', 'in Planung', 'u planiranju')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Pending offers', 'Ausstehende Angebote', 'Ponude na čekanju')}</div>
												<div className="fs-3 fw-bold text-warning count-up">{pendingOffers}</div>
												<small className="text-muted">{copy('Awaiting response', 'Wartet auf Antwort', 'Čeka odgovor')}</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">{copy('Accepted', 'Akzeptiert', 'Prihvaćeno')}</div>
												<div className="fs-3 fw-bold text-success count-up">{acceptedOffers}</div>
												<small className="text-muted">{copy('Won', 'Gewonnen', 'Dobiveno')}</small>
											</div>
										</div>
									</>
								)}
								{(userRole === 'gardener' || userRole === 'admin') && (
									<>
										{/* REMOVED: Tasks completed this week, Garden health – mock/synthetic. Restore when real task + sensor APIs exist. */}
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">
													{userRole === 'admin'
														? copy('Open projects', 'Offene Projekte', 'Otvoreni projekti')
														: copy('Active Projects', 'Aktive Projekte', 'Aktivni projekti')}
												</div>
												<div className="fs-3 fw-bold text-primary count-up">{activeProjects}</div>
												<small className="text-muted">
													{userRole === 'admin'
														? copy('Planning or in progress', 'Planung oder in Arbeit', 'Planiranje ili u tijeku')
														: `€${totalProjectValue.toLocaleString()} ${copy('value', 'Wert', 'vrijednost')}`}
												</small>
											</div>
										</div>
										<div className="col-6 col-md-3">
											<div className="text-center">
												<div className="text-muted small mb-1">
													{userRole === 'admin'
														? copy('Published on marketplace', 'Im Marktplatz veröffentlicht', 'Objavljeno na tržnici')
														: copy('Active Plans', 'Aktive Pläne', 'Aktivni planovi')}
												</div>
												<div className="fs-3 fw-bold text-info count-up">
													{userRole === 'admin' ? adminPublishedMarketplacePlans : activePlans}
												</div>
												<small className="text-muted">
													{userRole === 'admin'
														? `${adminPlanStats.draft} ${copy('drafts', 'Entwürfe', 'nacrta')} · ${adminPlanStats.total} ${copy('total in database', 'gesamt in der Datenbank', 'ukupno u bazi')}`
														: copy('Public listings', 'Öffentliche Einträge', 'Javne objave')}
												</small>
											</div>
										</div>
										{userRole === 'admin' && (
											<div className="col-6 col-md-3">
												<div className="text-center">
													<div className="text-muted small mb-1">
														{copy('Homepage views', 'Startseiten-Aufrufe', 'Pregledi početne stranice')}
													</div>
													<div className="fs-3 fw-bold text-success count-up">
														{adminHomepageActivity?.metrics.page_views ?? '—'}
													</div>
													<small className="text-muted">
														{adminHomepageActivity
															? `${adminHomepageActivity.metrics.unique_visitors} ${copy('unique visitors', 'eindeutige Besucher', 'jedinstvenih posjetitelja')} · ${copy('last 30 days', 'letzte 30 Tage', 'zadnjih 30 dana')}`
															: copy('hortera.org', 'hortera.org', 'hortera.org')}
													</small>
												</div>
											</div>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{userRole === 'admin' && adminGrowthSeries && (
				<section className="mt-4">
					<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
						<div>
							<h2 className="h5 mb-1">{copy('Growth over time', 'Wachstum im Zeitverlauf', 'Rast kroz vrijeme')}</h2>
							<p className="text-muted small mb-0">
								{copy(
									'One bar per month from Nov 2025 · hover a bar for details',
									'Ein Balken pro Monat ab Nov. 2025 · für Details über einen Balken fahren',
									'Jedan stupac po mjesecu od stu 2025. · pređite mišem za detalje'
								)}
							</p>
						</div>
						<div className="d-flex flex-wrap gap-2 align-items-center">
							<div className="btn-group btn-group-sm" role="group" aria-label={copy('Chart range', 'Zeitraum', 'Razdoblje')}>
								<button
									type="button"
									className={`btn ${growthRange === 6 ? 'btn-success' : 'btn-outline-success'}`}
									onClick={() => setGrowthRange(6)}
								>
									{copy('6 months', '6 Monate', '6 mjeseci')}
								</button>
								<button
									type="button"
									className={`btn ${growthRange === 12 ? 'btn-success' : 'btn-outline-success'}`}
									onClick={() => setGrowthRange(12)}
								>
									{copy('12 months', '12 Monate', '12 mjeseci')}
								</button>
								<button
									type="button"
									className={`btn ${growthRange === 'all' ? 'btn-success' : 'btn-outline-success'}`}
									onClick={() => setGrowthRange('all')}
								>
									{copy('Since Nov 2025', 'Seit Nov. 2025', 'Od stu 2025.')}
								</button>
							</div>
							<div className="btn-group btn-group-sm" role="group" aria-label={copy('Chart type', 'Darstellung', 'Prikaz')}>
								<button
									type="button"
									className={`btn ${growthMode === 'new' ? 'btn-dark' : 'btn-outline-dark'}`}
									onClick={() => setGrowthMode('new')}
								>
									{copy('New per month', 'Neu pro Monat', 'Novo po mjesecu')}
								</button>
								<button
									type="button"
									className={`btn ${growthMode === 'cumulative' ? 'btn-dark' : 'btn-outline-dark'}`}
									onClick={() => setGrowthMode('cumulative')}
								>
									{copy('Running total', 'Laufende Summe', 'Ukupno do danas')}
								</button>
							</div>
						</div>
					</div>
					<div className="row g-3">
						<div className="col-12 col-md-6">
							<MonthlyGrowthChart
								title={copy('Users registered', 'Registrierte Nutzer', 'Registrirani korisnici')}
								points={adminGrowthSeries.users}
								mode={growthMode}
								color="#0d6efd"
								onPointClick={(point) => openGrowthDrilldown('user_registrations', point.key)}
								helpNew={copy(
									'How many accounts were created each month',
									'Wie viele Konten pro Monat erstellt wurden',
									'Koliko je računa kreirano svaki mjesec'
								)}
								helpCumulative={copy(
									'Total accounts that existed by the end of each month',
									'Gesamtzahl der Konten am Monatsende',
									'Ukupan broj računa do kraja mjeseca'
								)}
							/>
						</div>
						<div className="col-12 col-md-6">
							<MonthlyGrowthChart
								title={copy('Plans created', 'Erstellte Pläne', 'Kreirani planovi')}
								points={adminGrowthSeries.plans}
								mode={growthMode}
								color="#198754"
								onPointClick={(point) => openGrowthDrilldown('plans_created', point.key)}
								helpNew={copy(
									'How many plans were created each month',
									'Wie viele Pläne pro Monat erstellt wurden',
									'Koliko je planova kreirano svaki mjesec'
								)}
								helpCumulative={copy(
									'Total plans in the database by the end of each month',
									'Gesamtzahl der Pläne am Monatsende',
									'Ukupan broj planova do kraja mjeseca'
								)}
							/>
						</div>
					</div>
				</section>
			)}

			{roleHighlights.length > 0 && (
				<section className="mt-4">
					<div className="d-flex justify-content-between align-items-center mb-3">
						<h2 className="h5 mb-0">{copy('My', 'Mein', 'Moj')} {roleDisplayLabel} {copy('Overview', 'Übersicht', 'pregled')}</h2>
						<span className="badge text-bg-success-subtle text-success-emphasis text-capitalize">{roleDisplayLabel}</span>
					</div>
					<div className="row g-3">
						{roleHighlights.map((highlight, index) => {
							// Map highlight titles to section IDs
							const sectionIdMap: Record<string, string> = {
								'Projects in progress': 'projects-in-progress',
								'Completed projects': 'completed-projects',
								'Planning & quotes': 'planning-quotes',
								'Offers pending': 'offers-pending',
								'Draft plans': 'draft-plans',
								'Total projects': 'total-projects',
								'Jobs in progress': 'jobs-in-progress',
								'Completed jobs': 'completed-jobs',
								'My offers': 'my-offers',
								'Assigned tasks': 'assigned-tasks',
								'Open plans to quote': 'open-plans-to-quote',
								'Portfolio projects': 'portfolio-projects',
								'Team active today': 'team-active-today',
								'Average offer (mid)': 'average-offer'
							};
							const sectionId = sectionIdMap[highlight.title];
							return (
								<div key={highlight.title} className="col-12 col-sm-6 col-xl-3">
									<div 
										className={`card h-100 border-top border-4 border-${highlight.variant}-subtle hover-lift card-shimmer animate-fade-in-up`}
										style={{ animationDelay: `${index * 0.1}s` }}
										{...(sectionId ? { 'data-section-id': sectionId } as React.HTMLAttributes<HTMLDivElement> : {})}
									>
										<div className="card-body">
											<div className="d-flex justify-content-between align-items-center">
												<span className="text-uppercase text-muted small">{highlight.title}</span>
												<i className={`bi ${highlight.icon} text-${highlight.variant} transition-all`} style={{ transition: 'transform 0.3s ease' }}></i>
											</div>
											<div className="display-6 my-2 count-up">{highlight.value}</div>
											{highlight.caption && <div className="text-muted small">{highlight.caption}</div>}
										</div>
									</div>
								</div>
							);
						})}
					</div>
					{roleDetailSection}
				</section>
			)}

			{/* REMOVED: Recent Tasks list + Sensors table – used mock @data/tasks and @data/metrics (temperature, humidity, moisture). Restore when real task + sensor/device data is available. */}
		</>
	);
}


